import React, { useState } from 'react';
import { 
  Form, 
  Input, 
  Button, 
  Card, 
  Typography, 
  Alert, 
  Checkbox, 
  Space,
  Row,
  Col,
  Divider
} from 'antd';
import { UserOutlined, LockOutlined, LoginOutlined } from '@ant-design/icons';
import { useAuth, useAuthActions } from '../../hooks/useAuthStore';
import { LoginRequest } from '@shared/types/auth';

const { Title, Text } = Typography;

interface LoginFormProps {
  onSuccess?: () => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSuccess }) => {
  const [form] = Form.useForm();
  const { isLoading, error } = useAuth();
  const { login, clearError } = useAuthActions();
  const [showCredentials, setShowCredentials] = useState(false);

  const handleSubmit = async (values: LoginRequest) => {
    try {
      clearError();
      await login(values);
      onSuccess?.();
    } catch (error) {
      // Error is handled by the store
      console.error('Login failed:', error);
    }
  };

  const testCredentials = [
    { role: 'Super Admin', username: 'superadmin', password: 'GetMyHive2025!' },
    { role: 'Operations', username: 'operations', password: 'Operations2025!' },
    { role: 'Security', username: 'security', password: 'Security2025!' }
  ];

  const fillTestCredentials = (username: string, password: string) => {
    form.setFieldsValue({ username, password });
  };

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <Card
        style={{
          width: '100%',
          maxWidth: 400,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          borderRadius: '16px',
          border: 'none'
        }}
        styles={{ body: { padding: '40px' } }}
      >
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '80px',
            height: '80px',
            background: 'linear-gradient(135deg, #FF6B9D, #C44569)',
            borderRadius: '50%',
            margin: '0 auto 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '32px',
            color: 'white'
          }}>
            🎯
          </div>
          <Title level={2} style={{ margin: 0, color: '#1a1a1a' }}>
            GetMyHive V2
          </Title>
          <Text type="secondary" style={{ fontSize: '16px' }}>
            Operasyon Merkezi
          </Text>
        </div>

        {error && (
          <Alert
            message={error}
            type="error"
            showIcon
            closable
            onClose={clearError}
            style={{ marginBottom: '24px' }}
          />
        )}

        <Form
          form={form}
          name="login"
          onFinish={handleSubmit}
          layout="vertical"
          size="large"
        >
          <Form.Item
            name="username"
            label="Kullanıcı Adı"
            rules={[
              { required: true, message: 'Lütfen kullanıcı adınızı girin!' }
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder="Kullanıcı adı"
              autoComplete="username"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="Şifre"
            rules={[
              { required: true, message: 'Lütfen şifrenizi girin!' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder="Şifre"
              autoComplete="current-password"
            />
          </Form.Item>

          <Form.Item name="remember" valuePropName="checked">
            <Checkbox>Beni hatırla</Checkbox>
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={isLoading}
              icon={<LoginOutlined />}
              block
              style={{
                height: '48px',
                borderRadius: '8px',
                background: 'linear-gradient(135deg, #FF6B9D, #C44569)',
                border: 'none',
                fontWeight: 600
              }}
            >
              {isLoading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
            </Button>
          </Form.Item>
        </Form>

        <Divider>Test Hesapları</Divider>

        <div style={{ textAlign: 'center' }}>
          <Button
            type="link"
            size="small"
            onClick={() => setShowCredentials(!showCredentials)}
            style={{ marginBottom: '16px' }}
          >
            {showCredentials ? 'Gizle' : 'Test hesaplarını göster'}
          </Button>

          {showCredentials && (
            <Space direction="vertical" style={{ width: '100%' }}>
              {testCredentials.map((cred, index) => (
                <Card
                  key={index}
                  size="small"
                  style={{ 
                    background: '#f5f5f5',
                    border: '1px solid #e0e0e0',
                    cursor: 'pointer'
                  }}
                  onClick={() => fillTestCredentials(cred.username, cred.password)}
                  hoverable
                >
                  <Row justify="space-between" align="middle">
                    <Col>
                      <Text strong>{cred.role}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {cred.username}
                      </Text>
                    </Col>
                    <Col>
                      <Button type="link" size="small">
                        Kullan
                      </Button>
                    </Col>
                  </Row>
                </Card>
              ))}
            </Space>
          )}
        </div>

        <div style={{ 
          marginTop: '24px', 
          textAlign: 'center',
          color: '#888',
          fontSize: '12px'
        }}>
          © 2025 GetMyHive V2 Operations Center
        </div>
      </Card>
    </div>
  );
};

export default LoginForm;
